import React, { useRef, useState } from "react";
import styled from "styled-components/macro";
import { CaretDownIcon } from "../Icons/Icons";

type CollapsibleSectionProps = {
  label: string;
  expandLabel: string;
  collapseLabel: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
};

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const Label = styled.div`
  font-size: 11px;
  line-height: 1;
  font-weight: 600;
  color: ${({ theme }) => theme.primaryTextColor};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ToggleButton = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  line-height: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  color: ${({ theme }) => theme.brandColor};
  &:hover svg {
    opacity: 0.85;
  }
`;

const CaretContainer = styled.span<{ isExpanded: boolean }>`
  display: inline-flex;
  transition: transform 0.2s ease-in-out;
  transform: ${({ isExpanded }) =>
    `translateY(1px) rotate(${isExpanded ? "180deg" : "0deg"})`};
`;

export function CollapsibleSection({
  label,
  expandLabel,
  collapseLabel,
  defaultExpanded = false,
  children,
}: CollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const contentIdRef = useRef(
    `collapsible-section-${Math.random().toString(36).slice(2, 10)}`
  );

  return (
    <>
      <Header>
        <ToggleButton
          type="button"
          onClick={() => setIsExpanded((previousState) => !previousState)}
          aria-label={isExpanded ? collapseLabel : expandLabel}
          aria-expanded={isExpanded}
          aria-controls={contentIdRef.current}
        >
          <Label>{label}</Label>
          <CaretContainer isExpanded={isExpanded}>
            <CaretDownIcon width={14} height={14} />
          </CaretContainer>
        </ToggleButton>
      </Header>
      {isExpanded && <div id={contentIdRef.current}>{children}</div>}
    </>
  );
}
